import type {
	Category,
	Item,
	KitchenStation,
	ModifierGroup,
	ReorderInput,
	SetAvailabilityInput,
	StaffMenu,
} from "@luckymenu/core";

/**
 * Changes to a cached `staff.catalog.listMenu` result, applied before the
 * server answers (optimistic) or with what it returned. Each keeps the lists
 * in the order the server gives: categories, groups and stations by
 * `sortOrder`, items by category order and then `sortOrder`.
 */

type Sorted = { id: string; sortOrder: number };

const bySortOrder = <T extends Sorted>(a: T, b: T) => a.sortOrder - b.sortOrder;

function sortItems(items: Item[], categories: readonly Category[]): Item[] {
	const rank = new Map(categories.map((c, i) => [c.id, i]));
	return [...items].sort(
		(a, b) =>
			(rank.get(a.categoryId) ?? Number.MAX_SAFE_INTEGER) -
				(rank.get(b.categoryId) ?? Number.MAX_SAFE_INTEGER) || a.sortOrder - b.sortOrder,
	);
}

function upsertInto<T extends Sorted>(list: readonly T[], row: T): T[] {
	const found = list.some((r) => r.id === row.id);
	return (found ? list.map((r) => (r.id === row.id ? row : r)) : [...list, row]).sort(bySortOrder);
}

/** The item as the server returned it, placed by its category and sort order. */
export function withItem(menu: StaffMenu, item: Item): StaffMenu {
	const others = menu.items.filter((i) => i.id !== item.id);
	return { ...menu, items: sortItems([...others, item], menu.categories) };
}

export function withCategory(menu: StaffMenu, category: Category): StaffMenu {
	const categories = upsertInto(menu.categories, category);
	return { ...menu, categories, items: sortItems(menu.items, categories) };
}

export function withModifierGroup(menu: StaffMenu, group: ModifierGroup): StaffMenu {
	return { ...menu, modifierGroups: upsertInto(menu.modifierGroups, group) };
}

export function withKitchenStation(menu: StaffMenu, station: KitchenStation): StaffMenu {
	return { ...menu, kitchenStations: upsertInto(menu.kitchenStations, station) };
}

export function withoutItem(menu: StaffMenu, id: string): StaffMenu {
	return { ...menu, items: menu.items.filter((i) => i.id !== id) };
}

export function withoutCategory(menu: StaffMenu, id: string): StaffMenu {
	return { ...menu, categories: menu.categories.filter((c) => c.id !== id) };
}

/** Removes the group and takes it off every item that offered it. */
export function withoutModifierGroup(menu: StaffMenu, id: string): StaffMenu {
	return {
		...menu,
		modifierGroups: menu.modifierGroups.filter((g) => g.id !== id),
		items: menu.items.map((i) =>
			i.modifierGroupIds.includes(id)
				? { ...i, modifierGroupIds: i.modifierGroupIds.filter((g) => g !== id) }
				: i,
		),
	};
}

/** Exactly `itemIds` offer the group afterwards; newly attached items get it last. */
export function withGroupItems(menu: StaffMenu, groupId: string, itemIds: readonly string[]) {
	const wanted = new Set(itemIds);
	return {
		...menu,
		items: menu.items.map((i) => {
			const has = i.modifierGroupIds.includes(groupId);
			if (wanted.has(i.id) && !has)
				return { ...i, modifierGroupIds: [...i.modifierGroupIds, groupId] };
			if (!wanted.has(i.id) && has)
				return { ...i, modifierGroupIds: i.modifierGroupIds.filter((g) => g !== groupId) };
			return i;
		}),
	};
}

/** The current availability of the row `input` names, or null if it is not in the menu. */
export function availabilityOf(menu: StaffMenu, input: Omit<SetAvailabilityInput, "isAvailable">) {
	switch (input.kind) {
		case "item":
			return menu.items.find((i) => i.id === input.id)?.isAvailable ?? null;
		case "variant":
			for (const item of menu.items)
				for (const v of item.variants) if (v.id === input.id) return v.isAvailable;
			return null;
		case "modifierOption":
			for (const group of menu.modifierGroups)
				for (const o of group.options) if (o.id === input.id) return o.isAvailable;
			return null;
	}
}

/** Sets one item, size or add-on option sold out or back, touching nothing else. */
export function withAvailability(menu: StaffMenu, input: SetAvailabilityInput): StaffMenu {
	const { id, isAvailable } = input;
	switch (input.kind) {
		case "item":
			return {
				...menu,
				items: menu.items.map((i) => (i.id === id ? { ...i, isAvailable } : i)),
			};
		case "variant":
			return {
				...menu,
				items: menu.items.map((i) =>
					i.variants.some((v) => v.id === id)
						? { ...i, variants: i.variants.map((v) => (v.id === id ? { ...v, isAvailable } : v)) }
						: i,
				),
			};
		case "modifierOption":
			return {
				...menu,
				modifierGroups: menu.modifierGroups.map((g) =>
					g.options.some((o) => o.id === id)
						? { ...g, options: g.options.map((o) => (o.id === id ? { ...o, isAvailable } : o)) }
						: g,
				),
			};
	}
}

/** `ids` first in that order, then the rest in their current order, numbered from 1. */
function renumber<T extends Sorted>(list: readonly T[], ids: readonly string[]): T[] {
	const named = new Map(ids.map((id, i) => [id, i]));
	const first = list
		.filter((r) => named.has(r.id))
		.sort((a, b) => (named.get(a.id) ?? 0) - (named.get(b.id) ?? 0));
	const rest = list.filter((r) => !named.has(r.id));
	return [...first, ...rest].map((r, i) => ({ ...r, sortOrder: i + 1 }));
}

/** The menu after a drag, as `staff.catalog.reorder` will leave it. */
export function withOrder(menu: StaffMenu, input: ReorderInput): StaffMenu {
	switch (input.kind) {
		case "categories": {
			const categories = renumber(menu.categories, input.ids);
			return { ...menu, categories, items: sortItems(menu.items, categories) };
		}
		case "modifierGroups":
			return { ...menu, modifierGroups: renumber(menu.modifierGroups, input.ids) };
		case "kitchenStations":
			return { ...menu, kitchenStations: renumber(menu.kitchenStations, input.ids) };
		case "items": {
			const moving = new Set(input.ids);
			const inCategory = menu.items
				.filter((i) => i.categoryId === input.categoryId || moving.has(i.id))
				.map((i) => ({ ...i, categoryId: input.categoryId }));
			const placed = renumber(inCategory, input.ids);
			const others = menu.items.filter((i) => !placed.some((p) => p.id === i.id));
			return { ...menu, items: sortItems([...others, ...placed], menu.categories) };
		}
	}
}

/** Moves one entry of a list, as a drag does. Out-of-range indexes change nothing. */
export function moveInList<T>(list: readonly T[], from: number, to: number): T[] {
	if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length)
		return [...list];
	const next = [...list];
	const [moved] = next.splice(from, 1) as [T];
	next.splice(to, 0, moved);
	return next;
}

/**
 * The reorder call for moving `activeId` onto `overId`'s place in `ids`, or
 * null when nothing moves. Both staff apps call this when a drag ends.
 */
export function reorderedIds(
	ids: readonly string[],
	activeId: string,
	overId: string,
): string[] | null {
	const from = ids.indexOf(activeId);
	const to = ids.indexOf(overId);
	if (from < 0 || to < 0 || from === to) return null;
	return moveInList(ids, from, to);
}
